import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import AuthScreen from './components/AuthScreen';
import { DataProvider, useData } from './context/DataContext';
import { DarkModeProvider } from './context/DarkModeContext';
import DarkModeToggle from './components/DarkModeToggle';
import LoadingSkeleton from './components/LoadingSkeleton';
import './App.css';

// Lazy load route components for code splitting
const KidsHome = lazy(() => import('./components/KidsHome'));
const KidTasks = lazy(() => import('./components/KidTasks'));
const FaceAuth = lazy(() => import('./components/FaceAuth'));
const MessageCenter = lazy(() => import('./components/MessageCenter'));
const RecordingStudio = lazy(() => import('./components/RecordingStudio'));
const FamilyChallenges = lazy(() => import('./components/FamilyChallenges'));
const SettingsPanel = lazy(() => import('./components/SettingsPanel'));
const AchievementsCelebration = lazy(() => import('./components/AchievementsCelebration'));
const VoiceCommands = lazy(() => import('./components/VoiceCommands'));
const InvitationAcceptance = lazy(() => import('./components/InvitationAcceptance'));
const ShoppingList = lazy(() => import('./components/ShoppingList'));
const FamilyAnalytics = lazy(() => import('./components/FamilyAnalytics'));

// Main App Content Component (needs to be inside DataProvider)
const AppContent = () => {
  const { isAuthenticated, isLoading, handleAuthSuccess, handleLogout, user } = useData();
  const [installPrompt, setInstallPrompt] = useState(null);

  const isInvitationRoute = typeof window !== 'undefined'
    && window.location.pathname.startsWith('/join/');

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setInstallPrompt(null);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen app-shell flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="w-20 h-20 bg-white/20 dark:bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          >
            <span className="text-4xl">🏆</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-fredoka text-white mb-2">TrophyTime</h2>
            <p className="text-white/80 font-nunito">Preparing your dashboard...</p>
            <div className="mt-4 w-32 h-1 bg-white/20 rounded-full mx-auto overflow-hidden">
              <motion.div
                className="h-full bg-white rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.2, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // One Router for the whole app. Remounting / tearing down Router on logout
  // used to throw into ErrorBoundary ("Oops! Something went wrong").
  return (
    <Router>
      {isInvitationRoute ? (
        <Routes>
          <Route path="/join/:token" element={<InvitationAcceptance />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : !isAuthenticated ? (
        <AuthScreen onAuthSuccess={handleAuthSuccess} />
      ) : (
        <div className="App app-shell relative">
          <motion.div
            className="fixed top-0 left-0 right-0 app-topbar text-center py-2 z-40"
            initial={{ y: -50 }}
            animate={{ y: 0 }}
          >
            <div className="flex items-center justify-between max-w-7xl mx-auto px-4">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-medium">🏆 TrophyTime</span>
                <span className="text-xs opacity-75">•</span>
                <span className="text-xs opacity-75">{user?.familyName || 'Family'}</span>
                <span className="text-xs opacity-75 hidden sm:inline">•</span>
                <span className="text-xs opacity-90 truncate hidden sm:inline" title={user?.email}>
                  👤 {user?.parentName || 'Parent'}{user?.email ? ` (${user.email})` : ''}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <DarkModeToggle />
                <button
                  onClick={handleLogout}
                  className="text-xs app-surface app-surface-hover px-2 py-1 rounded transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </motion.div>

          {installPrompt && (
            <motion.div
              className="fixed top-16 left-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 z-50"
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Install TrophyTime</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Get the full app experience</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setInstallPrompt(null)}
                    className="bg-white/20 text-white px-3 py-2 rounded-lg font-semibold"
                  >
                    Later
                  </button>
                  <button
                    onClick={handleInstallApp}
                    className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold"
                  >
                    Install
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          <div className="pt-12">
            <AnimatePresence mode="wait">
              <Suspense fallback={<LoadingSkeleton />}>
                <Routes>
                  <Route path="/" element={<KidsHome />} />
                  <Route path="/kid/:kidId" element={<KidTasks />} />
                  <Route path="/auth/:kidId" element={<FaceAuth />} />
                  <Route path="/record/:kidId/:taskId?" element={<RecordingStudio />} />
                  <Route path="/messages/:kidId" element={<MessageCenter />} />
                  <Route path="/challenges" element={<FamilyChallenges />} />
                  <Route path="/achievements/:kidId" element={<AchievementsCelebration />} />
                  <Route path="/settings" element={<SettingsPanel />} />
                  <Route path="/shopping" element={<ShoppingList />} />
                  <Route path="/analytics" element={<FamilyAnalytics />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </AnimatePresence>
          </div>

          <Suspense fallback={null}>
            <VoiceCommands />
          </Suspense>
        </div>
      )}
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <DarkModeProvider>
        <DataProvider>
          <AppContent />
          
          <Toaster
            position="top-center"
            gutter={8}
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1f2937',
                color: '#fff',
                borderRadius: '12px',
                fontSize: '16px',
                padding: '16px 20px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
                style: {
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
                style: {
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                },
              },
              loading: {
                iconTheme: {
                  primary: '#3b82f6',
                  secondary: '#fff',
                },
                style: {
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                },
              },
            }}
          />
        </DataProvider>
      </DarkModeProvider>
    </ErrorBoundary>
  );
}

export default App;
